import React, { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBack, Cancel, CancelOutlined } from '@mui/icons-material';
import ProductItem from './components/Services/ProductItem';
import BasicModal from './components/Modal';
import {
  deleteProduct,
  getAppSetting,
  getServiceProviders,
  updateServices,
  convertProductToV2,
  convertServicesBatchToV2,
  fetchCatalogPlansForProvider,
  findMatchingCatalogPlan,
  extractPlanIdFromLegacy,
  extractNetworkIdFromLegacy,
  extractProcessorSlugFromLegacy,
} from '../api/services';
import { getServices } from '../store/actions/services';
import AlertDialogSlide from './components/CustomAlert';
import { BRAND_IMAGES } from '../utils/assets';

export default function UpdateService() {
  const dispatch = useDispatch();
  const { category } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const currentService = queryParams.get('currentService');

  const [editedItem, setEditedItem] = React.useState(null);
  const [showEdit, setShowEdit] = React.useState(false);
  const [amount, setAmount] = React.useState('');
  const [vipAmount, setVipAmount] = React.useState('');
  const [isAirtimeSubService, setIsAirtimeSubService] = useState(false);
  const [cashback, setCashback] = React.useState('');
  const [referralBonus, setReferralBonus] = React.useState('');
  const [buyingAmount, setBuyingAmount] = React.useState('');
  const [validity, setValidity] = useState('');
  const [descriptions, setDescriptions] = useState('');

  const { services } = useSelector((state) => state.services);
  const [loading, setLoading] = React.useState(false);
  const [planId, setPlanId] = React.useState('');
  const [networkId, setNetworkId] = React.useState('');
  const [toggleSetPlanId, setToggleSetPlanId] = React.useState(false);

  const [identifier, setIdentifier] = useState('');
  const [currentProcessors, setCurrentProcessors] = useState([]);
  const [configuredProviders, setConfiguredProviders] = useState([]);
  const [providerCatalogPlans, setProviderCatalogPlans] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selectedCatalogPlanId, setSelectedCatalogPlanId] = useState('');
  const [convertingId, setConvertingId] = useState(null);
  const [batchConverting, setBatchConverting] = useState(false);

  // Filter ONLY legacy products (exclude products on V2 engine)
  const selectedCategory = services.filter(
    (service) =>
      service.category?.trim() === category.trim() &&
      service.service === currentService &&
      !service.useV2Engine
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [discount, setDiscount] = useState(0.0);

  const [currentAvailability, setCurrentAvailability] = useState(null);
  const [currentVisibility, setCurrentVisibility] = useState(null);
  const [name, setName] = useState('');
  const [feeAmount, setFeeAmount] = useState(0);

  // Lazy load catalog plans for a provider slug
  const loadProviderPlans = useCallback(
    async (slug) => {
      const cleanSlug = String(slug || '').toLowerCase().trim();
      if (!cleanSlug) {
        setProviderCatalogPlans([]);
        return;
      }
      setLoadingCatalog(true);
      try {
        const plans = await fetchCatalogPlansForProvider(cleanSlug, currentService || 'Data');
        setProviderCatalogPlans(plans || []);
      } catch (e) {
        setProviderCatalogPlans([]);
      } finally {
        setLoadingCatalog(false);
      }
    },
    [currentService]
  );

  // Combined processors/providers list
  const allAvailableProcessors = React.useMemo(() => {
    const list = [];
    const seen = new Set();

    (currentProcessors || []).forEach((pr) => {
      const slug = (pr.identifier || pr.name || '').toLowerCase().trim();
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        list.push({
          slug,
          name: pr.name || slug.toUpperCase(),
          identifier: pr.identifier || slug,
        });
      }
    });

    (configuredProviders || []).forEach((cp) => {
      const slug = (cp.slug || cp.name || '').toLowerCase().trim();
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        list.push({
          slug,
          name: cp.name || slug.toUpperCase(),
          identifier: slug,
        });
      }
    });

    return list;
  }, [currentProcessors, configuredProviders]);

  const effectivePlanId = toggleSetPlanId
    ? planId
    : (editedItem?.planId || extractPlanIdFromLegacy(editedItem) || '');

  const effectiveNetworkId = toggleSetPlanId
    ? networkId
    : extractNetworkIdFromLegacy(editedItem);

  // Matched plan from provider's catalog based on planId + networkId or smart match
  const matchedProviderPlan = React.useMemo(() => {
    if (!providerCatalogPlans || providerCatalogPlans.length === 0 || !editedItem) return null;
    const res = findMatchingCatalogPlan(editedItem, providerCatalogPlans, {
      planId: effectivePlanId,
      networkId: effectiveNetworkId,
      name,
    });
    return res.matchedPlan;
  }, [providerCatalogPlans, effectivePlanId, effectiveNetworkId, editedItem, name]);

  const handleConvertToV2 = async (product) => {
    setConvertingId(product.id);
    try {
      const res = await convertProductToV2(product.id, product, {
        candidateProviders: configuredProviders,
      });
      if (res.ok) {
        if (res.isVerified && res.matchedPlan) {
          const provName = res.matchedPlan.processorSlug?.toUpperCase() || res.providerSlug?.toUpperCase();
          toast.success(
            `✅ Matched on ${provName}: ${res.matchedPlan.planName || ''} (Plan ID: ${res.matchedPlan.planId}, Cost: ₦${res.matchedPlan.costPrice}) upgraded to V2!`
          );
        } else {
          toast.success(`${product.category} ${product.details?.name || ''} upgraded to V2! (Plan ID: ${res.planId})`);
        }
        dispatch(getServices());
      } else {
        toast.error(res.data?.message || 'Failed to convert to V2');
      }
    } catch (err) {
      toast.error('Error converting product to V2');
    } finally {
      setConvertingId(null);
    }
  };

  const handleConvertAllToV2 = async () => {
    if (selectedCategory.length === 0) return;
    setBatchConverting(true);
    try {
      const { successCount, total, verifiedCount, customCount } = await convertServicesBatchToV2(
        selectedCategory,
        { candidateProviders: configuredProviders }
      );
      if (verifiedCount > 0) {
        toast.success(
          `⚡ Converted ${successCount} of ${total} products to V2! (${verifiedCount} verified with provider catalog, ${customCount} custom)`
        );
      } else {
        toast.success(`Converted ${successCount} of ${total} products to V2!`);
      }
      dispatch(getServices());
    } catch (err) {
      toast.error('Failed to convert products to V2');
    } finally {
      setBatchConverting(false);
    }
  };

  const goBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/dashboard/services-legacy');
    }
  };

  const showDiscount = selectedCategory[0]?.service === 'Airtime' || selectedCategory[0]?.service === 'Electricity';
  const canRemove = selectedCategory[0]?.service === 'Data';

  const handleEditServiceAmount = (service) => {
    setEditedItem(service);
    const selectedProcessor =
      extractProcessorSlugFromLegacy(service) ||
      configuredProviders[0]?.slug ||
      currentProcessors[0]?.identifier ||
      '';
    setIdentifier(selectedProcessor);
    loadProviderPlans(selectedProcessor);

    const initialPlanId = service.planId || extractPlanIdFromLegacy(service) || '';
    const initialNetworkId =
      service.networkId !== undefined ? service.networkId : (extractNetworkIdFromLegacy(service) ?? '');
    setPlanId(initialPlanId);
    setNetworkId(initialNetworkId);
    setSelectedCatalogPlanId(initialPlanId);
    setToggleSetPlanId(false);

    setAmount(service.details?.amount || '');
    setVipAmount(service.vipAmount || '');
    setIsAirtimeSubService(service.isAirtimeSubService || false);
    setCashback(service.cashback || 0);
    setReferralBonus(service.referralBonus || 0);
    setName(service.details?.name || '');
    setDiscount(service.discount);
    setDescriptions(service.descriptions);
    setValidity(service.validity);
    setCurrentAvailability(service.available ? 'available' : 'unavailable');
    setCurrentVisibility(service.hidden ? 'hidden' : 'show');
    setBuyingAmount(service.buyingAmount || '');
    setShowEdit(true);
    setFeeAmount(service.fee || 0);
  };

  const getImageUrl = (item) => {
    if (item.service?.toLowerCase() !== 'data' && item.service?.toLowerCase() !== 'airtime') {
      return null;
    }

    return BRAND_IMAGES[item?.category?.toLowerCase()?.split(' ')[0]] || null;
  };

  const executeUpdateAmount = async (closeOnSuccess = true) => {
    // Validate buying amount has to be number and not empty
    if (!buyingAmount || Number.isNaN(Number(buyingAmount))) {
      toast.error('Please enter a valid buying amount');
      return false;
    }

    const imageUrl = getImageUrl(editedItem);

    const payload = {
      serviceId: editedItem.id,
      amount: amount || 0,
      vipAmount,
      isAirtimeSubService,
      cashback,
      referralBonus,
      discount,
      buyingAmount,
      validity,
      fee: feeAmount,
      descriptions,
      available: currentAvailability === 'available',
      hidden: currentAvailability === 'hidden',
      changePlanId: false,
      name: name?.trim(),
    };

    if (imageUrl) {
      payload.imageUrl = imageUrl;
    }

    if (identifier && currentService === 'Data') {
      payload.serviceProcessor = identifier;
    }

    if (toggleSetPlanId && currentService === 'Data') {
      payload.planId = planId;
      payload.networkId = networkId;
      payload.changePlanId = true;
    }

    const response = await updateServices(payload);
    if (response.ok) {
      if (closeOnSuccess) {
        setShowEdit(false);
        toast.success(response.data.message);
        dispatch(getServices());
      }
      return true;
    }

    toast.error(response.data.message);
    return false;
  };

  const handleUpdateAmount = async () => {
    setLoading(true);
    await executeUpdateAmount(true);
    setLoading(false);
  };

  const handleSaveAndUpgradeToV2 = async () => {
    setLoading(true);
    try {
      const updated = await executeUpdateAmount(false);
      if (!updated) return;

      const targetPlanId = toggleSetPlanId ? planId : (editedItem?.planId || extractPlanIdFromLegacy(editedItem));
      const targetNetworkId = toggleSetPlanId ? networkId : extractNetworkIdFromLegacy(editedItem);

      const res = await convertProductToV2(editedItem.id, editedItem, {
        processorSlug: identifier,
        planId: targetPlanId,
        networkId: targetNetworkId,
        costPrice: Number(buyingAmount),
        candidateProviders: configuredProviders,
        matchedPlan: matchedProviderPlan || undefined,
      });

      if (res.ok) {
        if (res.isVerified && res.matchedPlan) {
          toast.success(
            `✅ Matched & upgraded to V2 via ${res.providerSlug.toUpperCase()} (Plan #${res.matchedPlan.planId}, Cost: ₦${res.matchedPlan.costPrice})!`
          );
        } else {
          toast.success(`${editedItem.category} ${name || editedItem.details?.name || ''} upgraded to V2!`);
        }
        setShowEdit(false);
        dispatch(getServices());
      } else {
        toast.error(res.data?.message || 'Failed to convert to V2');
      }
    } catch (err) {
      toast.error('Error converting product to V2');
    } finally {
      setLoading(false);
    }
  };

  const continueRemovingProduct = async (option) => {
    if (!option) {
      cancelRemoveProduct();
      return;
    }
    setDeleteLoading(true);

    const request = await deleteProduct(selectedProduct.id);
    if (request.ok) {
      toast.success('delete success');
      dispatch(getServices());
      setSelectedProduct(null);
      setDeleteLoading(false);
      return;
    }

    setDeleteLoading(false);
    setSelectedProduct(null);
    toast.error('Error while deleting');
  };

  const cancelRemoveProduct = () => {
    setSelectedProduct(null);
  };

  const handleRemoveProduct = (product) => {
    setSelectedProduct(product);
  };

  const handleGetInitialData = useCallback(async () => {
    try {
      const [appSettingRes, providersRes] = await Promise.all([
        getAppSetting(),
        getServiceProviders(),
      ]);
      if (appSettingRes?.ok) {
        const { data } = appSettingRes.data;
        setCurrentProcessors(data?.service_processor?.current_processors || []);
      }
      if (providersRes?.ok) {
        setConfiguredProviders(providersRes.data?.data || []);
      }
    } catch (err) {
      console.error('Failed to load initial settings or providers', err);
    }
  }, []);

  React.useEffect(() => {
    handleGetInitialData();
  }, [handleGetInitialData]);

  return (
    <>
      <BasicModal isOpen={showEdit} toggleOpen={() => setShowEdit(!showEdit)}>
        <Stack spacing={2} style={{ height: '70vh', overflowY: 'scroll' }}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="h6">Update Product</Typography>
            <IconButton onClick={() => setShowEdit(!showEdit)}>
              <Cancel color="error" />
            </IconButton>
          </Stack>
          {editedItem && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel id="processor-select-label">Processor / Provider</InputLabel>
                <Select
                  labelId="processor-select-label"
                  id="processor-select"
                  value={identifier}
                  label="Processor / Provider"
                  onChange={(e) => {
                    const newSlug = e.target.value;
                    setIdentifier(newSlug);
                    loadProviderPlans(newSlug);
                  }}
                >
                  {allAvailableProcessors?.map((pr) => (
                    <MenuItem key={pr.slug} value={pr.slug}>
                      {pr.name} ({pr.slug})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingCatalog && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Searching {identifier?.toUpperCase()} catalog...
                  </Typography>
                </Box>
              )}

              {/* Provider verification banner */}
              {!loadingCatalog && matchedProviderPlan && (
                <Box sx={{ p: 1.5, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '10px' }}>
                  <Typography variant="subtitle2" sx={{ color: '#166534', fontWeight: 700 }}>
                    ✅ Product Verified on {identifier?.toUpperCase()}: {matchedProviderPlan.planName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#15803D', display: 'block', mt: 0.5 }}>
                    Plan ID: <strong>{matchedProviderPlan.planId}</strong>
                    {matchedProviderPlan.networkId !== undefined ? ` | Network ID: ${matchedProviderPlan.networkId}` : ''}
                    {matchedProviderPlan.costPrice !== undefined ? ` | Provider Cost: ₦${matchedProviderPlan.costPrice}` : ''}
                    {matchedProviderPlan.validity ? ` | Validity: ${matchedProviderPlan.validity}` : ''}
                  </Typography>
                  {matchedProviderPlan.costPrice !== undefined &&
                    Number(buyingAmount) !== Number(matchedProviderPlan.costPrice) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setBuyingAmount(matchedProviderPlan.costPrice)}
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          py: 0.25,
                          fontSize: '0.75rem',
                          borderColor: '#16A34A',
                          color: '#16A34A',
                          '&:hover': { borderColor: '#15803D', bgcolor: 'rgba(22, 163, 74, 0.04)' },
                        }}
                      >
                        Sync Buying Price to ₦{matchedProviderPlan.costPrice}
                      </Button>
                    )}
                </Box>
              )}

              {!loadingCatalog && !matchedProviderPlan && providerCatalogPlans.length > 0 && (
                <Box sx={{ p: 1.5, bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px' }}>
                  <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600, display: 'block' }}>
                    ⚠️ Current Plan ID #{effectivePlanId || 'none'} not matched in {identifier?.toUpperCase()} catalog ({providerCatalogPlans.length} plans available).
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#B45309' }}>
                    Pick a plan from the provider's catalog below, or toggle manual Plan ID.
                  </Typography>
                </Box>
              )}

              {/* Provider catalog plans dropdown */}
              {providerCatalogPlans.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel id="catalog-plan-select-label">Select from {identifier?.toUpperCase()} Catalog Plans</InputLabel>
                  <Select
                    labelId="catalog-plan-select-label"
                    value={selectedCatalogPlanId}
                    label={`Select from ${identifier?.toUpperCase()} Catalog Plans`}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setSelectedCatalogPlanId(selectedId);
                      const found = providerCatalogPlans.find((cp) => String(cp.planId) === String(selectedId));
                      if (found) {
                        setPlanId(found.planId);
                        if (found.networkId !== undefined) {
                          setNetworkId(found.networkId);
                        }
                        setToggleSetPlanId(true);
                        if (found.costPrice !== undefined) {
                          setBuyingAmount(found.costPrice);
                        }
                        if (!name) {
                          setName(found.planName);
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>-- Select a plan from {identifier?.toUpperCase()} catalog --</em>
                    </MenuItem>
                    {providerCatalogPlans.map((cp) => (
                      <MenuItem key={`${cp.planId}_${cp.networkId || ''}`} value={cp.planId}>
                        {cp.planName || cp.planId} (ID: {cp.planId}
                        {cp.networkId !== undefined ? `, Net: ${cp.networkId}` : ''}
                        {cp.costPrice !== undefined ? `, ₦${cp.costPrice}` : ''})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* If it is Data category show this switch for airtime sub-service */}
              {editedItem?.service?.toLowerCase() === 'data' && (
                <FormControlLabel
                  control={
                    <Switch checked={isAirtimeSubService} onChange={(e) => setIsAirtimeSubService(e.target.checked)} />
                  }
                  label="Airtime Sub Service"
                />
              )}

              {/* Setup user to be able to change plan id after toggling that */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<CancelOutlined />}
                onClick={() => {
                  setToggleSetPlanId(!toggleSetPlanId);
                  if (toggleSetPlanId) {
                    setPlanId('');
                    setNetworkId('');
                    setSelectedCatalogPlanId('');
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                {toggleSetPlanId ? 'Toggle Custom Plan ID: ON' : 'Toggle Custom Plan ID: OFF'}
              </Button>

              {toggleSetPlanId && (
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Network ID"
                    type="text"
                    value={networkId}
                    onChange={(event) => {
                      setNetworkId(event.target.value);
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Plan ID"
                    type="text"
                    value={planId}
                    onChange={(event) => {
                      setPlanId(event.target.value);
                      setSelectedCatalogPlanId(event.target.value);
                    }}
                  />
                </Stack>
              )}
            </>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product Availability</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentAvailability}
              label="Product Availability"
              onChange={(event) => setCurrentAvailability(event.target.value)}
            >
              <MenuItem value={'available'}>Available</MenuItem>
              <MenuItem value={'unavailable'}>Unavailable</MenuItem>
            </Select>
          </FormControl>
          <TextField
            name="planName"
            value={name}
            label="Name"
            onChange={(e) => {
              const { value } = e.target;
              // Only allow alphanumeric characters (letters and numbers)
              if (/^[a-zA-Z0-9\s.]*$/.test(value)) {
                setName(value);
              }
            }}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="demo-simple-select-label">Product Visibility</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={currentVisibility}
              label="Product Visibility"
              onChange={(event) => setCurrentVisibility(event.target.value)}
            >
              <MenuItem value={'show'}>Show Product</MenuItem>
              <MenuItem value={'hidden'}>Hide Product</MenuItem>
            </Select>
          </FormControl>
          {showDiscount ? (
            <>
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Discount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={discount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    if (Number(event.target.value) >= 100) {
                      setDiscount(100);
                      return;
                    }
                    setDiscount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Fee (Percentage)"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={feeAmount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    if (Number(event.target.value) >= 100) {
                      setFeeAmount(100);
                      return;
                    }
                    setFeeAmount(event.target.value);
                  }
                }}
              />
            </>
          ) : (
            <>
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Amount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={amount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '') {
                    setAmount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Vip Amount"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={vipAmount}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '') {
                    setVipAmount(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Cashback"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={cashback}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    setCashback(event.target.value);
                  }
                }}
              />
              <TextField
                fullWidth
                sx={{ my: '20px' }}
                id="outlined-required"
                label="Referral Bonus"
                type="number"
                onPaste={(e) => {
                  e.preventDefault();
                  return false;
                }}
                onCopy={(e) => {
                  e.preventDefault();
                  return false;
                }}
                value={referralBonus}
                onChange={(event) => {
                  if (Number(event.target.value) || event.target.value === '' || event.target.value === '0') {
                    setReferralBonus(event.target.value);
                  }
                }}
              />
            </>
          )}

          <TextField
            fullWidth
            sx={{ my: '20px' }}
            id="outlined-required"
            label="Buying Price"
            type="number"
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            value={buyingAmount}
            onChange={(event) => {
              setBuyingAmount(event.target.value);
            }}
          />

          {showDiscount && (
            <Typography variant="subtitle2" color="coral">
              For Airtime and Electricity orders, the <b>Buying Price</b> represents the
              <b> profit percentage</b> you earn on each sale (e.g. 1% of ₦100 = ₦1 profit).
            </Typography>
          )}

          <TextField
            name="validity"
            label="Validity"
            value={validity}
            onChange={(e) => {
              setValidity(e.target.value);
            }}
          />

          <TextField
            name="description"
            label="Description"
            multiline
            minRows={3}
            value={descriptions}
            onChange={(e) => {
              setDescriptions(e.target.value);
            }}
          />

          <Stack spacing={'10px'}>
            <Button variant={'contained'} marginLeft="10px" onClick={handleUpdateAmount} disabled={loading}>
              {loading ? <CircularProgress size="20px" /> : 'Update Service'}
            </Button>
            <Button
              variant={'contained'}
              onClick={handleSaveAndUpgradeToV2}
              disabled={loading}
              sx={{
                bgcolor: '#16A34A',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': { bgcolor: '#15803D' },
              }}
            >
              {loading ? <CircularProgress size="20px" color="inherit" /> : '⚡ Save & Convert to V2'}
            </Button>
            <Button variant={'outlined'} onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </BasicModal>
      <AlertDialogSlide
        dialogTitle={selectedProduct && `Delete ${selectedProduct.service} Item`}
        dialog={
          selectedProduct &&
          `Are you sure you want to remove ${selectedProduct.category} ${selectedProduct.details?.name || ''}`
        }
        loading={deleteLoading}
        onClose={cancelRemoveProduct}
        onOption={continueRemovingProduct}
        isOpen={selectedProduct !== null}
      />
      <Box padding={'20px'}>
        <Stack direction={'row'} alignItems={'center'} mb={2}>
          <IconButton onClick={goBack}>
            <ArrowBack />
          </IconButton>
          <Typography sx={{ cursor: 'pointer', fontWeight: 600 }} onClick={goBack}>
            Go back to Legacy Services
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={3}
        >
          <div>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Update {category} (Legacy)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {selectedCategory.length} legacy product{selectedCategory.length === 1 ? '' : 's'} remaining. Converted products are managed in Services (V2).
            </Typography>
          </div>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {selectedCategory.length > 0 && (
              <Button
                variant="contained"
                onClick={handleConvertAllToV2}
                disabled={batchConverting}
                sx={{
                  bgcolor: '#16A34A',
                  color: '#FFFFFF',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#15803D' },
                }}
              >
                {batchConverting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `⚡ Convert All (${selectedCategory.length}) to V2`
                )}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/dashboard/services/category/${(currentService || 'data').toLowerCase()}`
                )
              }
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#0066FE',
                color: '#0066FE',
              }}
            >
              Open in Services (V2)
            </Button>
          </Stack>
        </Stack>

        {selectedCategory.length === 0 ? (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: '20px',
              bgcolor: '#FFFFFF',
              border: '1px solid #ECEEF1',
              mt: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              All {category} products migrated to V2! 🎉
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mb: 3, maxWidth: 500, mx: 'auto' }}>
              There are no legacy products remaining in this category. All products have been upgraded to the V2 Multi-Processor engine with automatic failover routing.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `/dashboard/services/category/${(currentService || 'data').toLowerCase()}`
                  )
                }
                sx={{
                  bgcolor: '#0066FE',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0052CC' },
                }}
              >
                View {currentService || 'Services'} in V2
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/services-legacy')}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Return to Legacy Overview
              </Button>
            </Stack>
          </Box>
        ) : (
          <Grid container spacing={'10px'} mt={1}>
            {selectedCategory.map((item) => (
              <Grid key={item.id} item xs={6} sm={3} md={2}>
                <ProductItem
                  canRemove={canRemove}
                  onRemoveProduct={() => handleRemoveProduct(item)}
                  onItemPress={() => handleEditServiceAmount(item)}
                  onConvertToV2={() => handleConvertToV2(item)}
                  isConverting={convertingId === item.id}
                  title={`${category} ${showDiscount ? '' : item.details.name}`}
                  amount={item.details.amount}
                  discount={item?.discount}
                  imageUrl={item.imageUrl}
                  showDiscount={showDiscount}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </>
  );
}
